/**
 * Замок Чахлика Невмирущого: rooms cut into a mountain.
 *
 * **Not a building on a hill.** The whole box is filled with проклятий камінь
 * first and the rooms are then carved out of it, which is the opposite of how
 * every other structure in this game is written and the only way to get what
 * was asked for: a castle *inside* the rock, with one gate showing on the
 * mountainside and everything else behind stone. `raise` clears the box
 * before it builds, so the mountain's own rock goes and this massif takes its
 * place - and because the massif is solid to its own edges, from outside it
 * is simply the mountain with a door in it.
 *
 * Three levels, seven rooms, and a stair between them:
 *
 *   lower   the gate hall, the скарбниця, the підземелля
 *   middle  the головний зал - his - and two chambers off it
 *   upper   the вежа, which is the one part that breaks the surface
 *
 * Two скелети stand in each room. Their posts are `CASTLE_POSTS`, and the
 * throne is `CASTLE_THRONE`: written down here rather than searched for at
 * spawn time, because where a guard stands is a fact about the building.
 *
 * Pure, like the кремль: local coordinates and a callback, no world, no
 * chunks, no renderer.
 */

import { BlockId, type DoorFacing } from "../block/registry.js";
import { offsetFor, type PlannedBuilding } from "./blueprint.js";

/** Across, up and back. Odd across, so the gate has a middle. */
export const CASTLE_SPAN = 45;
export const CASTLE_HEIGHT = 34;

/**
 * How far the box is sunk below the surface it is placed on.
 *
 * One, and it used to be six, which put the gate *underground*. The opening
 * is cut at local y 1 to 5, so a drop of six left the whole arch between
 * five blocks and one block **below** the hillside outside it - a door you
 * could neither see nor walk through - while the solid rock above it stood
 * twenty-six blocks proud as a bare black wall. Flying out to look at it is
 * what found that: "there is no castle", and the castle was the wall.
 *
 * At one, the threshold sits a course into the slope and the arch stands
 * open above it. What keeps the rest of it inside the mountain is the drape
 * in `raiseCastle`, not the drop.
 */
export const CASTLE_DROP = 1;

/**
 * How far in front of the gate the road is cut, and the country grown.
 *
 * One number for both on purpose. The road climbs from the ground at this
 * distance up to the threshold at one course a block, so it also sets the
 * steepest bank the gate can sit behind and still be walked into. Sixteen
 * covers every hillside measured; what it must never exceed is the country
 * `raiseCastle` grows, because a road laid over columns that do not exist
 * yet is written into nothing at all.
 */
export const APPROACH_REACH = 16;

type Put = (x: number, y: number, z: number, id: number) => void;

/** A solid run, inclusive at both ends. */
function box(
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number, id: number,
): void {
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) put(x, y, z, id);
    }
  }
}

/**
 * Hollow a room and line it.
 *
 * The walls are brick and the inside is air; the floor is brick as well,
 * because a floor of the same rough stone as the mountain reads as a cave
 * and these are rooms.
 */
function room(
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number,
): void {
  // The lining, one course proud of the hollow on every side.
  box(put, x0 - 1, x1 + 1, y0 - 1, y1 + 1, z0 - 1, z1 + 1, BlockId.CursedBrick);
  box(put, x0, x1, y0, y1, z0, z1, BlockId.Air);
  box(put, x0 - 1, x1 + 1, y0 - 1, y0 - 1, z0 - 1, z1 + 1, BlockId.CursedBrick);
  light(put, x0, x1, y0, y1, z0, z1);
}

/**
 * Lamps round the walls of a room, close enough together to see by.
 *
 * Every room used to get one or two placed by hand, which on a зала
 * twenty-eight blocks across lights the middle and leaves the ends black -
 * "add some torches, it is dark there". A lamp reaches as far as a
 * смолоскип, so they go every six blocks round the walls and no room is
 * bigger than the sum of its lights.
 *
 * Against the wall at head height rather than on the floor: on the floor
 * they are the first thing a fight knocks you into, and a лiхтар underfoot
 * lights the ceiling.
 */
function light(
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number,
): void {
  const at = Math.min(y0 + 3, y1);
  const STEP = 6;
  /*
   * Set *into* the wall, not stood against it.
   *
   * The first version put them on the room's own edge columns, which is the
   * air beside the wall - so every one of them was a glowing cube hanging in
   * the room. Reported as "some weird boxes flying there", and they were.
   * One course further out is the lining itself, and a lamp that replaces a
   * brick reads as a light in the wall.
   */
  for (let x = x0 + 1; x <= x1 - 1; x += STEP) {
    put(x, at, z0 - 1, BlockId.CursedLamp);
    put(x, at, z1 + 1, BlockId.CursedLamp);
  }
  for (let z = z0 + 1; z <= z1 - 1; z += STEP) {
    put(x0 - 1, at, z, BlockId.CursedLamp);
    put(x1 + 1, at, z, BlockId.CursedLamp);
  }
}

/**
 * A passage: three tall, and it carves and nothing else.
 *
 * It used to line itself with brick first, the way `room` does, and that
 * lining is what sealed every door in the place: a corridor running up to a
 * room wrote a brick wall across the last course *inside* the room, so the
 * air either side of it never met. Fourteen guards in seven rooms and twelve
 * of them were walled in - which a slice through the building does not show,
 * and walking it does.
 *
 * A corridor cut through solid rock does not need a lining anyway. What is
 * around it is the mountain.
 */
function hall(
  put: Put, x0: number, x1: number, y0: number, z0: number, z1: number,
): void {
  box(put, x0, x1, y0, y0 + 2, z0, z1, BlockId.Air);
}

/**
 * A stair climbing along +z, cut out of the rock.
 *
 * Steps rather than a ladder: the player steps up one block at a time and
 * everything in this game can do that, including whatever is chasing them.
 */
function stair(
  put: Put, x0: number, x1: number, yFrom: number, zFrom: number, rise: number,
): void {
  for (let i = 0; i < rise; i++) {
    const y = yFrom + i;
    const z = zFrom + i;
    // The tread, and headroom above it.
    box(put, x0 - 1, x1 + 1, y - 1, y - 1, z, z, BlockId.CursedBrick);
    box(put, x0, x1, y, y + 2, z, z, BlockId.Air);
    box(put, x0 - 1, x1 + 1, y + 3, y + 3, z, z, BlockId.CursedBrick);
  }
}

/**
 * Where a cell of the plan actually lands in the world.
 *
 * The one place that knows. `castleAt` is the point `raise` was given, not a
 * corner of anything: the plan is anchored twenty-two blocks along x and a
 * course further on in z, so `at.x + lx` is off by twenty-two and `at.z + lz`
 * by one. Everything that puts something *in* the замок got this wrong the
 * same way, and the symptom was the замок standing empty - fourteen guards
 * and Чахлик placed into solid rock outside their own walls, where nothing
 * can be seen and nothing can be fought.
 *
 * It asks `offsetFor`, which is what `raise` asks, so the two cannot drift.
 */
export function castleCell(
  at: { x: number; y: number; z: number },
  local: readonly [number, number, number],
): { x: number; y: number; z: number } {
  const { dx, dz } = offsetFor(CASTLE, 0, local[0], local[2]);
  return { x: at.x + dx, y: at.y + local[1], z: at.z + dz };
}

/** A lamp on a wall, which is the only light in here. */
function lamp(put: Put, x: number, y: number, z: number): void {
  put(x, y, z, BlockId.CursedLamp);
}

/**
 * Where the two guards of each room stand, and where he sits.
 *
 * Room order matches the plan below. The pairs are set apart across the room
 * rather than side by side: two guards in one doorway is one guard drawn
 * twice, and the shove that keeps orcs out of each other's cells cannot fix
 * a pair that was put down on the same block to begin with.
 */
export const CASTLE_POSTS: readonly (readonly [number, number, number])[] = [
  // The gate hall.
  [18, 1, 8], [26, 1, 8],
  // Скарбниця.
  [6, 1, 20], [12, 1, 26],
  // Підземелля.
  [32, 1, 20], [38, 1, 26],
  // Головний зал, either side of the throne.
  [14, 9, 20], [30, 9, 20],
  // Спальня.
  [6, 9, 36], [12, 9, 40],
  // Бібліотека.
  [32, 9, 36], [38, 9, 40],
  // Вежа.
  [20, 17, 20], [24, 17, 24],
  // Найвища кімната, at the top of the second stair.
  [20, 27, 20], [24, 27, 24],
];

/**
 * Where Чахлик is: on the dais, not in it.
 *
 * The dais is a course of brick at y=9, so y=9 is the *block* and y=10 is
 * where somebody stands on it. Naming y=9 put him inside his own furniture,
 * and the walk through the building said so.
 */
export const CASTLE_THRONE: readonly [number, number, number] = [22, 10, 26];

/** How many rooms have guards in them. Two apiece. */
export const CASTLE_ROOMS = CASTLE_POSTS.length / 2;

function planCastle(put: Put, facing: DoorFacing): void {
  void facing;
  const span = CASTLE_SPAN - 1;
  const top = CASTLE_HEIGHT - 1;

  /*
   * The mountain first, and the rooms out of it afterwards.
   *
   * Solid to every edge. `raise` has already cleared whatever rock was here,
   * so without this the castle would stand in a box-shaped hole in the
   * hillside with daylight all round it.
   */
  /*
   * The massif: solid, to every edge of the box.
   *
   * There was a version of this that tapered as it rose, on the theory that
   * a crag would emerge from the hillside with the вежа in it. Measured, it
   * does not: the world is a hundred and twenty-eight blocks tall, a
   * mountain in it stands at about a hundred and fifteen, and a castle cut
   * into that mountain with room for its own thirty-four courses sits with
   * its roof *below* the summit. The taper's only effect was to leave the
   * rock hollow around the shape - air pockets inside a hill, because
   * `raise` clears the whole box first and a taper then declines to fill it
   * back in.
   *
   * So: solid. What you see from outside is a mountain with a gate in it,
   * which is what was asked for, and the вежа is the highest room rather
   * than a thing on the skyline.
   */
  box(put, 0, span, 0, top, 0, span, BlockId.CursedStone);

  // ---- lower: the gate, the treasury, the dungeon ------------------------
  // The gate hall, and the gate itself cut through the front face.
  room(put, 16, 28, 1, 7, 1, 12);
  box(put, 20, 24, 1, 5, 0, 1, BlockId.Air);
  // An arch around it, so the opening reads as a door and not a hole.
  box(put, 19, 25, 6, 6, 0, 0, BlockId.CursedBrick);
  box(put, 19, 19, 1, 6, 0, 0, BlockId.CursedBrick);
  box(put, 25, 25, 1, 6, 0, 0, BlockId.CursedBrick);
  lamp(put, 19, 4, 1);
  lamp(put, 25, 4, 1);

  // Скарбниця and підземелля, one either side, off the spine.
  room(put, 4, 14, 1, 7, 16, 28);
  lamp(put, 5, 4, 22);
  // Heaped in the far corner, clear of where the guards stand: a post
  // inside the treasure is a guard buried in it, which is what the walk
  // through the building found.
  box(put, 4, 7, 1, 1, 25, 28, BlockId.Gilt);
  room(put, 30, 42, 1, 7, 16, 28);
  lamp(put, 41, 4, 22);

  // ---- middle: his hall, and two chambers --------------------------------
  /*
   * The головний зал, and it is deliberately the biggest thing in here.
   *
   * Twenty-eight across: room to move, room for fire to cross, and pillars
   * to put between you and it. A boss fight in a corridor is a fight nobody
   * can lose or win.
   */
  room(put, 8, 36, 9, 15, 8, 32);
  for (const px of [12, 22, 32]) {
    for (const pz of [12, 20, 28]) {
      box(put, px - 1, px + 1, 9, 15, pz - 1, pz + 1, BlockId.CursedBrick);
      lamp(put, px, 13, pz - 1);
    }
  }
  // The dais he stands on.
  box(put, 20, 24, 9, 9, 24, 28, BlockId.CursedBrick);

  room(put, 4, 14, 9, 15, 34, 42);
  lamp(put, 5, 12, 38);
  room(put, 30, 42, 9, 15, 34, 42);
  lamp(put, 41, 12, 38);

  // ---- upper: the вежа, which is the part that shows --------------------
  room(put, 18, 26, 17, 25, 18, 26);
  /*
   * And the room over it, which used to be a hole in the air.
   *
   * What was here was a box of air carved above the вежа with no floor under
   * it: measured, nought standing cells out of nought - not a sealed room,
   * no room at all, just more height on the tower. From inside the game it
   * reads as a room at the top that connects to nothing, which is how it was
   * reported.
   *
   * It belongs *here*, with the rooms, and not down among the passages where
   * it was first written: a room lines itself with brick, so a room written
   * after the corridor that reaches it lays a wall across its own doorway.
   * That is the same fault that sealed twelve guard posts once before, and
   * writing the rooms first and the ways between them last is the whole
   * reason this file is ordered the way it is.
   */
  room(put, 18, 26, 27, 30, 18, 26);
  lamp(put, 19, 21, 19);
  lamp(put, 25, 21, 25);

  /*
   * ---- and now the ways between them, carved last ----------------------
   *
   * Last on purpose: a passage has to win over the wall it goes through, and
   * the rooms above wrote their own floors and ceilings over everything.
   * Carving the circulation first and the rooms afterwards is how you get a
   * castle whose stairs arrive inside a wall - which is exactly what the
   * first version of this did, and what looking at a slice of it showed.
   */
  // The spine, gate hall to the back of the rock.
  hall(put, 21, 23, 1, 12, 38);
  lamp(put, 21, 3, 18);
  lamp(put, 23, 3, 30);
  // And into the two side rooms off it.
  // Reaching a course into the room at one end and the spine at the other,
  // so the air actually meets rather than stopping against a wall.
  hall(put, 13, 22, 1, 21, 23);
  hall(put, 22, 31, 1, 21, 23);

  /*
   * The stair up, in a shaft behind the hall rather than inside it.
   *
   * Inside, its air punched a hole through the hall's own floor and you
   * arrived in a pit. Behind the back wall it climbs through rock, and one
   * doorway lets it in at the top.
   */
  stair(put, 21, 23, 2, 33, 7);
  // The landing, and the doorway through the hall's back wall.
  box(put, 20, 24, 9, 11, 36, 39, BlockId.Air);
  box(put, 21, 23, 9, 11, 32, 36, BlockId.Air);
  lamp(put, 20, 10, 37);

  // Off the hall into the two chambers behind it.
  box(put, 10, 12, 9, 11, 32, 34, BlockId.Air);
  box(put, 32, 34, 9, 11, 32, 34, BlockId.Air);

  /*
   * And the stair to the вежа, which does climb inside the hall.
   *
   * It is allowed to here: what it breaks through is the hall's *ceiling*,
   * and a hole in a ceiling with a stair in it is a stairwell. The passage
   * at the top walks out into the tower.
   */
  stair(put, 21, 23, 10, 10, 7);
  box(put, 21, 23, 17, 19, 16, 19, BlockId.Air);

  /*
   * And on up to the top room, behind the вежа rather than through it.
   *
   * The same shape as the first stair and for the same reason: climbing
   * inside the tower would put its air through the tower's own floor. This
   * one leaves by the back wall, climbs through the rock behind, and walks
   * in at the top.
   */
  box(put, 21, 23, 18, 20, 26, 30, BlockId.Air);
  stair(put, 21, 23, 19, 30, 9);
  /*
   * The passage in at the top runs *beside* the stairwell, not over it.
   *
   * Over it, three of its columns had the stair's own air underneath and so
   * no floor at all - a corridor with a hole in it that a slice shows as
   * solid, because the hole is one course down. Two blocks to the east the
   * rock is untouched, and that is what it walks on.
   */
  hall(put, 24, 26, 28, 26, 39);
  lamp(put, 27, 29, 31);

  /*
   * The вежа's own shaft, and a slot to see down the valley from.
   *
   * It does not break the surface: there is no room above a mountain this
   * castle is inside of. What it is, is the top of the place - the last
   * room, the highest light, and a window cut through the rock to the north
   * so it is not blind.
   */
  box(put, 20, 24, 20, 23, 15, 18, BlockId.Air);
  box(put, 17, 27, 31, 31, 17, 27, BlockId.CursedBrick);
  // The battlement round the top of the shaft: a ruin nobody looks down on,
  // but it is what the room is, and somebody digging in from above meets it.
  for (let i = 17; i <= 27; i += 2) {
    put(i, 31, 17, BlockId.CursedBrick);
    put(i, 31, 27, BlockId.CursedBrick);
    put(17, 31, i, BlockId.CursedBrick);
    put(27, 31, i, BlockId.CursedBrick);
  }
  // And a light at the top of it.
  lamp(put, 22, 29, 19);
}

/**
 * How tall the замок actually is in each column of its footprint.
 *
 * The plan fills its whole box solid and the rooms are cut out of that, so
 * "how tall is it here" is not the box: it is the highest thing in this
 * column that is *part of the building* - a room's air, its brick, a lamp.
 * Everything above that is fill, and the fill is what gets cut back to the
 * hill's own shape.
 *
 * Siting needs this. Scoring a site against a flat box top says the замок is
 * thirty-four tall at its corners, where in truth it is a low room under a
 * lot of spare rock, so sites were being picked that scored well and stood
 * out of the ground anyway. Twenty-nine per cent and thirty-five were chosen
 * over sixes and elevens that way.
 *
 * Worked out once from the plan and kept: the plan does not depend on where
 * the замок goes.
 */
let profile: Int16Array | null = null;
export function castleProfile(): Int16Array {
  if (profile !== null) return profile;
  const out = new Int16Array(CASTLE_SPAN * CASTLE_SPAN).fill(-1);
  CASTLE.plan((lx, ly, lz, id) => {
    if (id !== BlockId.Air && id !== BlockId.CursedBrick &&
        id !== BlockId.CursedLamp) return;
    if (lx < 0 || lz < 0 || lx >= CASTLE_SPAN || lz >= CASTLE_SPAN) return;
    const at = lz * CASTLE_SPAN + lx;
    if (ly > out[at]!) out[at] = ly;
  }, 0);
  profile = out;
  return out;
}

export const CASTLE: PlannedBuilding = {
  name: "castle",
  label: "замок Чахлика",
  // The gate is in the middle of the front face, and that is what the anchor
  // means: where the thing is measured from when it is put down.
  anchor: { x: 22, z: 0 },
  size: { x: CASTLE_SPAN, y: CASTLE_HEIGHT, z: CASTLE_SPAN },
  /*
   * Everything in the box goes, which here is a mountainside.
   *
   * The point of the building *is* to replace rock with rooms, so there is
   * nothing to spare and nothing to refuse over. It is raised once, when the
   * world is made, before anybody has built anything to lose.
   */
  clears: "everything",
  foundation: BlockId.CursedStone,
  plan: planCastle,
};
